import { getValueForLocale, literalOverrides, optionOverrides, optionValueOverrides, unsupportedLocaleToClosetFormat, unsupportedOptionValueOverrides, valueOverrides, } from "../../data/format-overrides.js";
const OriginalDateTimeFormat = Intl.DateTimeFormat || function () { };
export class DateTimeFormat extends OriginalDateTimeFormat {
    constructor(...args) {
        const [locales, options, ...restArgs] = args;
        const overrideLocales = getOverrideLocales(locales);
        const newOptions = getOverriddenOptions(locales, options, ...restArgs);
        super(overrideLocales, newOptions, ...restArgs);
        if (locales == null) {
            this.originalLocale = this.resolvedOptions().locale;
        }
        else if (typeof locales === "string") {
            this.originalLocale = locales;
        }
        else {
            this.originalLocale = locales[0];
        }
    }
    format(...args) {
        return this.formatToParts(...args)
            .map((part) => part.value)
            .join("");
    }
    formatToParts(...args) {
        const [arg0] = args;
        if (arg0 == null) {
            return super.formatToParts(...args);
        }
        const inputDate = new Date(arg0);
        const options = this.resolvedOptions();
        const overrideOptions = { ...options, locale: this.originalLocale };
        const originalParts = super.formatToParts(...args);
        return originalParts.map((part) => applyOverridesToFormatPart(inputDate, overrideOptions, part));
    }
    formatRange(...args) {
        return this.formatRangeToParts(...args)
            .map((part) => part.value)
            .join("");
    }
    formatRangeToParts(...args) {
        const [arg0, arg1] = args;
        // TODO: deal with bigints
        if (arg0 == null || arg1 == null || typeof arg0 === "bigint" || typeof arg1 === "bigint") {
            return super.formatRangeToParts(...args);
        }
        const startDate = new Date(arg0);
        const endDate = new Date(arg1);
        const options = this.resolvedOptions();
        const overrideOptions = { ...options, locale: this.originalLocale };
        const originalParts = super.formatRangeToParts(...args);
        return originalParts.map((part) => {
            if (part.source === "endRange") {
                return applyOverridesToFormatPart(endDate, overrideOptions, part);
            }
            return applyOverridesToFormatPart(startDate, overrideOptions, part);
        });
    }
}
/**
 * Injects a local we prefer to use in the event that the engine doesn't support
 * the requested locale
 */
function getOverrideLocales(locales) {
    if (locales == null) {
        return locales;
    }
    // Looks like it is supported, skip all our custom code
    const isLocaleSupported = OriginalDateTimeFormat.supportedLocalesOf?.(locales).length !== 0;
    if (isLocaleSupported) {
        return locales;
    }
    // Get the first locale and see if it's in the lookup map
    const locale = typeof locales === "string" ? locales : locales[0];
    const overrideLocale = getValueForLocale(locale, unsupportedLocaleToClosetFormat);
    if (overrideLocale == null) {
        return locales;
    }
    if (typeof locales === "string") {
        return [overrideLocale, locales];
    }
    else {
        return [overrideLocale, ...locales];
    }
}
/**
 * When we have unsupported locales, we need to translate dateStyle="long" to it's individual parts (year, month, day)
 * So that the numerical formatting will be correct
 */
function getOverriddenOptions(...args) {
    const [locale, options, ...rest] = args;
    let currOptions = options;
    currOptions = getOverriddenValueOptions(locale, currOptions, ...rest);
    currOptions = getOverriddenGeneralOptions(locale, currOptions, ...rest);
    return currOptions;
}
function getOverriddenGeneralOptions(...args) {
    const [locales, options] = args;
    if (locales == null || locales.length === 0) {
        // No locale, let Intl API fail
        return options;
    }
    const locale = typeof locales === "string" ? locales : locales[0];
    const optionOverride = getValueForLocale(locale, optionOverrides);
    if (optionOverride == null) {
        // No overrides, exit
        return options;
    }
    // Spread in only the values that aren't defined, we don't want to overwrite
    // if someone explicitly requested an option
    const newOptions = { ...options };
    for (const [untypedKey, value] of Object.entries(optionOverride)) {
        const key = untypedKey;
        if (newOptions[key] != null) {
            continue;
        }
        newOptions[key] = value;
    }
    return newOptions;
}
/**
 * When we have unsupported locales, we need to translate dateStyle="long" to it's individual parts (year, month, day)
 * So that the numerical formatting will be correct
 */
function getOverriddenValueOptions(...args) {
    const [locales, options] = args;
    if (locales == null || locales.length === 0 || options == null) {
        return options;
    }
    const locale = typeof locales === "string" ? locales : locales[0];
    const isLocaleSupported = OriginalDateTimeFormat.supportedLocalesOf?.(locales).length !== 0;
    const optionValueOverride = isLocaleSupported
        ? getValueForLocale(locale, optionValueOverrides)
        : getValueForLocale(locale, unsupportedOptionValueOverrides);
    if (optionValueOverride == null) {
        return options;
    }
    let newOptions = { ...options };
    for (const untypedKey in newOptions) {
        const key = untypedKey;
        if (newOptions[key] == null) {
            continue;
        }
        const optionOverride = optionValueOverride[key];
        if (optionOverride == null) {
            continue;
        }
        const optionValue = newOptions[key];
        if (typeof optionValue !== "string") {
            continue;
        }
        const replacementOptions = optionOverride[optionValue];
        if (replacementOptions == null) {
            continue;
        }
        delete newOptions[key];
        newOptions = {
            ...newOptions,
            ...replacementOptions,
        };
    }
    return newOptions;
}
function applyOverridesToFormatPart(inputDate, options, formatPart) {
    const { type: formatPartType, value } = formatPart;
    const { locale } = options;
    if (formatPartType === "literal" || formatPartType === "dayPeriod") {
        const literalOverride = getValueForLocale(locale, literalOverrides);
        const newValue = literalOverride?.[value];
        if (newValue != null) {
            return {
                ...formatPart,
                value: newValue,
            };
        }
        return formatPart;
    }
    const styleType = getStyle(options, formatPartType);
    if (styleType === undefined) {
        return formatPart;
    }
    // Get the override if it exists, otherwise return the existing format part
    const allOverridesForLocale = getValueForLocale(locale, valueOverrides);
    const d = allOverridesForLocale?.[formatPartType];
    const overridesForPart = d?.[styleType];
    if (overridesForPart == null) {
        return formatPart;
    }
    // Look up the new value since we do have a lookup map for the override
    const lookupValue = getValueForPart(formatPartType, inputDate);
    const newValue = overridesForPart[lookupValue] ?? value;
    return {
        ...formatPart,
        value: newValue,
    };
}
/**
 * Helper function to get style from the resolved options since the types of keyof options and
 * Intl.DateTimeFormatPartTypes don't intersect
 * @param options
 * @param formatPartType
 * @returns
 */
function getStyle(options, formatPartType) {
    // @ts-expect-error Yea we're mixing up types
    const value = options[formatPartType];
    if (typeof value !== "string") {
        return undefined;
    }
    return value;
}
/**
 * Returns the numerical value associated with the part requested
 */
function getValueForPart(formatPartType, inputDate) {
    switch (formatPartType) {
        case "day":
        case "weekday":
            return inputDate.getDay();
        case "month":
            return inputDate.getMonth();
        default:
            throw new Error(`getValueForPart not implemented for ${formatPartType}`);
    }
}
